import { store } from '../../../../app/store';
import { extractErrorMessage } from '../../../../utils/errorUtils';
import { wiseApi, type WiseSendResult } from '../../api/wiseApi';
import type { CommandHandler } from '../types';
import {
  CONFIRMATION_UNAVAILABLE,
  findInvoiceRow,
  getGridRows,
  getSelectedInvoiceIds,
  invoiceLabel,
} from './wiseInvoiceRows';

/** One readable summary line per invoice, failures first so they are not missed. */
export const formatWiseSendResult = (result: WiseSendResult): string => {
  const total = result.SentCount + result.FailedCount;
  const header =
    result.FailedCount === 0
      ? `Sent ${result.SentCount} invoice(s) to Wise.`
      : `Sent ${result.SentCount} of ${total} invoice(s) to Wise.`;

  const lines = [...result.Results]
    .sort((a, b) => Number(a.Success) - Number(b.Success))
    .map((item) => {
      const label = item.InvoiceNumber || `#${item.InvoiceId}`;
      return item.Success
        ? `${label}: transfer ${item.TransferId} created for ${item.Amount} ${item.Currency}`
        : `${label}: ${item.Error || 'failed'}`;
    });

  return [header, ...lines].join('\n');
};

export const sendToWiseCommandHandler: CommandHandler = async (_command, context) => {
  const invoiceIds = getSelectedInvoiceIds(context);
  if (invoiceIds.length === 0) {
    return { success: false, error: 'Please select at least one invoice.' };
  }

  // Sending creates real transfers, so it never runs without the user's confirmation.
  if (!context.requestConfirmation) {
    return { success: false, error: CONFIRMATION_UNAVAILABLE };
  }

  // Every selected id goes to the server, which skips invoices already sent; the grid may hold
  // stale rows or only the current page, so it is not used to decide.
  const rows = getGridRows(context);
  const numbers = invoiceIds.map((id) => invoiceLabel(findInvoiceRow(rows, id), id)).join(', ');
  const confirmed = await context.requestConfirmation(
    `Create transfers in Wise for ${invoiceIds.length} invoice(s): ${numbers}? ` +
      'Invoices already sent to Wise are skipped.'
  );
  if (!confirmed) {
    return { success: false };
  }

  context.setLoading?.(true);
  try {
    const result = await store
      .dispatch(wiseApi.endpoints.sendInvoicesToWise.initiate(invoiceIds))
      .unwrap();

    if (result.SentCount === 0) {
      return { success: false, error: formatWiseSendResult(result) };
    }

    // Partial failures still refresh the grid so the new marks become visible.
    return { success: true, shouldRefresh: true, message: formatWiseSendResult(result) };
  } catch (error) {
    return {
      success: false,
      error: extractErrorMessage(error, 'Failed to send invoices to Wise.'),
    };
  } finally {
    context.setLoading?.(false);
  }
};
